'use client';

import { forwardRef } from 'react';
import { cn } from '@/hen-ui/utils';
import type { SubNavPanelProps, SubNavProps } from './types';
import { subNavVariants } from './subnav.variants';
import { DesktopLayout } from './DesktopLayout';
import { ColumnsLayout } from './ColumnsLayout';

/**
 * SubNav with the panel options the Navbar sets (authored menu content, the
 * heading toggle, and the section transition). Kept off the public SubNav
 * props.
 */
export const SubNavPanel = forwardRef<HTMLDivElement, SubNavPanelProps>(
  (
    {
      title,
      sections,
      cta,
      className,
      dividedLastColumn = false,
      shadow,
      rounded,
      layout = 'sections',
      menuContent,
      showHeading = true,
      menuTransition,
      contentClassName,
      columnWidth,
      panelStyle,
      ...props
    },
    ref,
  ) => {
    if (!sections.length) return null;

    const content =
      layout === 'columns' ? (
        <ColumnsLayout
          title={title}
          columns={sections}
          cta={cta}
          dividedLastColumn={dividedLastColumn}
          menuContent={menuContent}
          showHeading={showHeading}
          columnWidth={columnWidth}
          panelStyle={panelStyle}
        />
      ) : (
        <DesktopLayout
          title={title}
          sections={sections}
          cta={cta}
          dividedLastColumn={dividedLastColumn}
          menuContent={menuContent}
          showHeading={showHeading}
          menuTransition={menuTransition}
          columnWidth={columnWidth}
          panelStyle={panelStyle}
        />
      );

    return (
      <div
        ref={ref}
        className={cn(subNavVariants({ shadow, rounded }), className)}
        {...props}
      >
        {contentClassName ? (
          <div className={contentClassName}>{content}</div>
        ) : (
          content
        )}
      </div>
    );
  },
);

SubNavPanel.displayName = 'SubNavPanel';

/**
 * SubNav component — a flyout navigation panel with a sidebar of sections
 * and a multi-column content area for each section. With
 * `layout='columns'`, `sections` are rendered as the panel's columns and the
 * sidebar is omitted.
 *
 * Supports featured content cards, action/highlighted link variants,
 * a configurable CTA footer, and a responsive mobile accordion layout.
 *
 * @example
 * <SubNav
 *   title="Degrees"
 *   sections={sections}
 *   cta={{ text: 'Find a program', buttonText: 'Search', href: '/search' }}
 * />
 */
export const SubNav = forwardRef<HTMLDivElement, SubNavProps>((props, ref) => (
  <SubNavPanel ref={ref} {...props} />
));

SubNav.displayName = 'SubNav';
